// Keep operator-facing Sentinel error copy in one place.

export type SentinelErrorStage = 'blueprint' | 'response' | 'dispatch';

/** Code that needs action instead of another retry. */
export const BLUEPRINT_REFINEMENT_NOT_APPLIED = 'BLUEPRINT_REFINEMENT_NOT_APPLIED';

/** Copy for stable backend codes and coarse gRPC types. */
const ERROR_COPY: Record<string, string> = {
  SENTINEL_ASSIGNMENT_FAILED: 'Sentinel could not take this ticket in the helpdesk.',
  SENTINEL_CLAIM_FAILED: 'Sentinel could not start on this ticket.',
  DRAFT_REFINEMENT_NOT_APPLIED:
    'Couldn’t update the reply because an action was already completed.',
  HITL_ACTION_OUTCOME_UNKNOWN:
    'The approved action could not be confirmed. Check the ticket before trying again.',
  HITL_ACTION_COMPLETED_DRAFT_FAILED:
    'The action completed, but Sentinel could not generate a reply.',
  [BLUEPRINT_REFINEMENT_NOT_APPLIED]:
    'Couldn’t update the blueprint — the previous version has been restored and is still usable.',
  DISPATCH_SEND_FAILED: 'The reply could not be sent to the helpdesk.',
  DISPATCH_CLOSE_FAILED: 'The reply was sent but the ticket could not be closed.',
  BLUEPRINT_STATE_EXPIRED: 'This ticket’s blueprint session expired.',
  ERROR_TYPE_SUITABLE_RULE: 'No workflow covers this ticket, so Sentinel could not answer it.',
  ERROR_TYPE_GUARDRAIL: 'A guardrail blocked this reply, so this ticket needs a human answer.',
  ERROR_TYPE_ESCALATION: 'This ticket needs a human agent.',
  ERROR_TYPE_BLOCKED_INTENT: 'Sentinel is not allowed to handle this kind of request.',
  ERROR_TYPE_SYSTEM: 'Something went wrong on our side.',
  ERROR_TYPE_VALIDATION: 'Sentinel could not work with this ticket’s details.',
  ERROR_TYPE_NOT_FOUND: 'Sentinel could not find the data this ticket needs.',
  ERROR_TYPE_UNAUTHORIZED: 'Your session is no longer valid.',
  ERROR_TYPE_FORBIDDEN: 'You do not have access to run Sentinel on this ticket.',
  // ERROR_TYPE_UNSPECIFIED falls through to the stage copy.
};

/** Copy used when no known error code is available. */
const STAGE_COPY: Record<SentinelErrorStage, string> = {
  blueprint: 'Error: unable to generate a blueprint. Do you want to retry?',
  response: 'Error: unable to generate a response. Do you want to retry?',
  dispatch: 'We could not confirm this reply reached the helpdesk — check the ticket there before replying again.',
};

// These decisions need a person instead of a retry.
const NOT_ANSWERED_CODES = new Set([
  'ERROR_TYPE_ESCALATION',
  'ERROR_TYPE_GUARDRAIL',
  'ERROR_TYPE_SUITABLE_RULE',
  'ERROR_TYPE_BLOCKED_INTENT',
  'AUTOMATION_DISPATCH_BLOCKED',
]);

// These actions already ran and cannot be retried or handed off safely.
const ACTION_ALREADY_RAN_CODES = new Set(['HITL_ACTION_OUTCOME_UNKNOWN', 'HITL_ACTION_COMPLETED_DRAFT_FAILED']);

/** List the safe actions for an error. */
export interface SentinelErrorActions {
  retry: boolean;
  manual: boolean;
}

function hasCode(codes: Set<string>, code: string | null): boolean {
  return code !== null && (codes.has(code) || codes.has(`ERROR_TYPE_${code}`));
}

/** Return short operator-facing copy for a ticket failure. */
export function sentinelErrorMessage(code: string | null, stage: SentinelErrorStage): string {
  if (!code) return STAGE_COPY[stage];
  // Brahma sends the short code, so try the full type name too.
  return ERROR_COPY[code] ?? ERROR_COPY[`ERROR_TYPE_${code}`] ?? STAGE_COPY[stage];
}

export function sentinelErrorActions(code: string | null): SentinelErrorActions {
  if (hasCode(NOT_ANSWERED_CODES, code)) return { retry: false, manual: true };
  if (hasCode(ACTION_ALREADY_RAN_CODES, code)) return { retry: false, manual: false };
  // Other failures can be retried or handed off.
  return { retry: true, manual: true };
}
